export { ContentState as default };

/**
* Content State
*
* Defines the state of the content during data fetch
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class ContentState {
  /**
   * Defines if content is loading
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {Boolean}
   */
  loading = true;

  /**
   * Defines if content is loading
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {Boolean}
   */
  loading_background = false;

  /**
   * Defines if content has loaded
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {Boolean}
   */
  loaded = false;

  /**
   * Stores loadded callback functions
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {Array}
   */
  loaded_callbacks = [];

  /**
   * Defines if content is saving
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {Boolean}
   */
  saving = false;

  /**
   * Defines if content is saving inline
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {Boolean}
   */
  saving_inline = false;

  /**
   * Defines if content is loading failed
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {Boolean}
   */
  error = false;

  /**
   * Error callback function when registered
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {Function}
   */
  error_callback;

  /**
   * Defines if content is empty
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {Boolean}
   */
  not_found = false;

  /**
   * Registers an on Loaded function to be called
   * When loaded finishes
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  onLoaded(callable) {
    this.setLoadedCallback(callable);

    if (this.loaded) {
      this.callLoadedCallbacks();
    }
  }

  /**
   * Registers an on Error function to be called
   * when error retry is called
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Function} callable
   *
   * @return {void}
   */
  onErrorRetry(callable) {
    this.error_callback = callable;

    if (this.error) {
      this.error_callback();
    }
  }

  /**
   * Sets a loadded callback function
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {Function} callable
   *
   * @return {void}
   */
  setLoadedCallback(callback) {
    this.loaded_callbacks.push(callback);
  }

  /**
   * Calls all registered loaded calbacks
   *
   * @private
   *
   * @since 7.0.0
   *   *
   * @return {void}
   */
  callLoadedCallbacks() {
    this.loaded_callbacks.forEach((callback) => {
      callback();
    })

    this.loaded_callbacks = [];
  }

  /**
   * Sets the loading to true
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void>}
   */
  setLoading() {
    this.loaded  = false;
    this.loading = true;
  }

  /**
   * Sets Background loading state
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Boolean} saving
   *
   * @return {void>}
   */
  setLoadingBackground(value) {
    this.loading_background = value;
  }

  /**
   * Sets the content saving state
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Boolean} saving
   *
   * @return {void}
   */
  setSaving(saving) {
    this.saving = saving;
  }

  /**
   * Sets the content saving inline state
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Boolean} saving
   *
   * @return {void}
   */
  setSavingInline(saving) {
    this.saving_inline = saving;
  }

  /**
   * Sets the loaded state
   *
   * Calls the onloaded method when registered
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Boolean} success success state of the fetch
   *
   * @return {void}
   */
  setLoadedState(success) {
    this.loaded    = success;
    this.error     = !success;
    this.loading   = false;
    this.not_found = false;

    if (this.loaded) {
      this.callLoadedCallbacks();
    }
  }

  /**
   * Sets the content state to not found
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Boolean} empty
   *
   * @return {void}
   */
  setFoundState(value) {
    this.not_found = !value;
    this.loaded    = value;
  }

  /**
   * Calls the on error callback function if registered
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Boolean} empty
   *
   * @return {void}
   */
  errorRetry() {
    if (this.error && this.error_callback) {
      this.error_callback();
    }
  }
}